/**
 * Indonesian Syllabifier (Pemecah Suku Kata Bahasa Indonesia)
 * Deterministic, offline-capable algorithm based on Indonesian phonotactics
 * with exception overrides and digraph recognition (ng, ny, sy, kh).
 */

export interface SyllabificationResult {
  original: string;
  normalized: string;
  syllables: string[];
  confidence: 'high' | 'medium' | 'low';
  source: 'algorithm' | 'exception' | 'fallback';
}

// Curated high-frequency dictionary exceptions & verified words
const EXCEPTION_MAP: Record<string, string[]> = {
  // Common 2-syllables
  baju: ['ba', 'ju'],
  bola: ['bo', 'la'],
  buku: ['bu', 'ku'],
  meja: ['me', 'ja'],
  roti: ['ro', 'ti'],
  susu: ['su', 'su'],
  topi: ['to', 'pi'],
  kaki: ['ka', 'ki'],
  mata: ['ma', 'ta'],
  kuda: ['ku', 'da'],
  sapi: ['sa', 'pi'],
  ikan: ['i', 'kan'],
  apel: ['a', 'pel'],
  daun: ['da', 'un'],
  pintu: ['pin', 'tu'],
  pagi: ['pa', 'gi'],
  sore: ['so', 're'],
  malam: ['ma', 'lam'],
  siang: ['si', 'ang'],
  jalan: ['ja', 'lan'],
  makan: ['ma', 'kan'],
  minum: ['mi', 'num'],
  rumah: ['ru', 'mah'],
  tidur: ['ti', 'dur'],
  masuk: ['ma', 'suk'],
  keluar: ['ke', 'lu', 'ar'],
  buka: ['bu', 'ka'],
  tutup: ['tu', 'tup'],
  parkir: ['par', 'kir'],
  pasar: ['pa', 'sar'],
  toko: ['to', 'ko'],
  bakso: ['bak', 'so'],
  mobil: ['mo', 'bil'],
  kereta: ['ke', 're', 'ta'],
  sepeda: ['se', 'pe', 'da'],
  sekolah: ['se', 'ko', 'lah'],
  kelapa: ['ke', 'la', 'pa'],
  celana: ['ce', 'la', 'na'],
  kelinci: ['ke', 'lin', 'ci'],
  pelangi: ['pe', 'la', 'ngi'],
  matahari: ['ma', 'ta', 'ha', 'ri'],
  bermain: ['ber', 'ma', 'in'],
  membaca: ['mem', 'ba', 'ca'],
  menulis: ['me', 'nu', 'lis'],
  belajar: ['be', 'la', 'jar'],
  selamat: ['se', 'la', 'mat'],
  datang: ['da', 'tang'],
  terima: ['te', 'ri', 'ma'],
  kasih: ['ka', 'sih'],
  tolong: ['to', 'long'],
  senang: ['se', 'nang'],
  pintar: ['pin', 'tar'],
  hebat: ['he', 'bat'],
  teman: ['te', 'man'],
  keluarga: ['ke', 'lu', 'ar', 'ga'],
  ayah: ['a', 'yah'],
  ibu: ['i', 'bu'],
  kakak: ['ka', 'kak'],
  adik: ['a', 'dik'],
  kakek: ['ka', 'kek'],
  nenek: ['ne', 'nek'],
};

const VOWELS = new Set(['a', 'i', 'u', 'e', 'o']);
const DIGRAPHS = ['ng', 'ny', 'sy', 'kh'];
const HIATUS_PAIRS = new Set(['ea', 'eo', 'ia', 'iu', 'io', 'ua', 'ui', 'ue']);

/**
 * Normalizes input text for reading engine
 */
export function normalizeWord(word: string): string {
  return word
    .toLowerCase()
    .normalize('NFC')
    .replace(/[^a-z\-]/g, '')
    .trim();
}

/**
 * Checks if character is an Indonesian vowel
 */
export function isVowel(char: string): boolean {
  return VOWELS.has(char.toLowerCase());
}

/**
 * Segments a single Indonesian word into syllables.
 */
export function syllabifyWord(rawWord: string): SyllabificationResult {
  const normalized = normalizeWord(rawWord);

  if (!normalized) {
    return {
      original: rawWord,
      normalized: '',
      syllables: [rawWord],
      confidence: 'low',
      source: 'fallback',
    };
  }

  // Handle hyphenated reduplication e.g. "hati-hati", "anak-anak"
  if (normalized.includes('-')) {
    const parts = normalized.split('-');
    const subResults = parts.map(part => syllabifySingleWord(part));
    const combinedSyllables = subResults.flatMap(r => r.syllables);
    const confidence = subResults.every(r => r.confidence === 'high') ? 'high' : 'medium';
    return {
      original: rawWord,
      normalized,
      syllables: combinedSyllables,
      confidence,
      source: 'algorithm',
    };
  }

  return syllabifySingleWord(normalized, rawWord);
}

function syllabifySingleWord(word: string, original = word): SyllabificationResult {
  // 1. Check verified exception map
  if (EXCEPTION_MAP[word]) {
    return {
      original,
      normalized: word,
      syllables: EXCEPTION_MAP[word],
      confidence: 'high',
      source: 'exception',
    };
  }

  // 2. Fallback for very short words (1-2 chars)
  if (word.length <= 2 && !(word.length === 2 && HIATUS_PAIRS.has(word))) {
    return {
      original,
      normalized: word,
      syllables: [word],
      confidence: 'high',
      source: 'algorithm',
    };
  }

  // 3. Rule-based segmentation
  // Replace digraphs temporarily with placeholder markers
  // ng -> 1, ny -> 2, sy -> 3, kh -> 4
  let encoded = word;
  encoded = encoded.replace(/ng/g, 'ŋ');
  encoded = encoded.replace(/ny/g, 'ɲ');
  encoded = encoded.replace(/sy/g, 'ʃ');
  encoded = encoded.replace(/kh/g, 'x');

  const chars = Array.from(encoded);
  const isV = (c: string) => isVowel(c);

  // Identify syllable break indices
  const breaks: number[] = [];

  for (let i = 0; i < chars.length - 1; i++) {
    const cur = chars[i];
    const nxt = chars[i + 1];
    const nxt2 = chars[i + 2];
    const nxt3 = chars[i + 3];

    // Pattern 1: V-V (adjacent distinct vowels that are not standard diphthongs like ai, au, oi)
    if (isV(cur) && isV(nxt)) {
      const pair = cur + nxt;
      if (HIATUS_PAIRS.has(pair)) {
        breaks.push(i + 1);
      }
    }
    // Pattern 2: V-CV (Vowel followed by single Consonant then Vowel)
    else if (isV(cur) && !isV(nxt) && nxt2 && isV(nxt2)) {
      breaks.push(i + 1);
    }
    // Pattern 3: VC-CV (Vowel followed by two Consonants then Vowel, e.g. bak-so, pin-tu)
    else if (isV(cur) && !isV(nxt) && nxt2 && !isV(nxt2) && nxt3 && isV(nxt3)) {
      // Check for common onset consonant clusters (bl, br, dr, gl, gr, kl, kr, pl, pr, tr)
      const cluster = nxt + nxt2;
      const validOnsetClusters = ['bl', 'br', 'dr', 'gl', 'gr', 'kl', 'kr', 'pl', 'pr', 'tr', 'sp', 'st'];
      if (validOnsetClusters.includes(cluster)) {
        breaks.push(i + 1); // e.g. sa-kro
      } else {
        breaks.push(i + 2); // e.g. bak-so, pin-tu
      }
    }
  }

  // Split into syllable pieces
  const syllables: string[] = [];
  let start = 0;
  for (const b of breaks) {
    if (b > start && b < chars.length) {
      syllables.push(chars.slice(start, b).join(''));
      start = b;
    }
  }
  if (start < chars.length) {
    syllables.push(chars.slice(start).join(''));
  }

  // Restore digraphs
  const restoredSyllables = syllables.map(s =>
    s
      .replace(/ŋ/g, 'ng')
      .replace(/ɲ/g, 'ny')
      .replace(/ʃ/g, 'sy')
      .replace(/x/g, 'kh')
  );

  // Validate reconstruction
  if (restoredSyllables.join('') === word) {
    return {
      original,
      normalized: word,
      syllables: restoredSyllables,
      confidence: 'high',
      source: 'algorithm',
    };
  }

  // Safe fallback if rules produce mismatch
  return {
    original,
    normalized: word,
    syllables: [word],
    confidence: 'low',
    source: 'fallback',
  };
}

/**
 * Tokenizes a sentence into words, punctuation, and spaces
 */
export interface TokenizedSentence {
  original: string;
  words: {
    text: string;
    normalized: string;
    syllables: string[];
    isWord: boolean;
  }[];
}

export function tokenizeSentence(sentence: string): TokenizedSentence {
  // Regex to split words while retaining punctuation & whitespaces
  const tokenRegex = /([a-zA-Z\-]+|[^a-zA-Z\s]+|\s+)/g;
  const matches = sentence.match(tokenRegex) || [sentence];

  const words = matches.map(token => {
    const isWord = /^[a-zA-Z\-]+$/.test(token);
    if (isWord) {
      const result = syllabifyWord(token);
      return {
        text: token,
        normalized: result.normalized,
        syllables: result.syllables,
        isWord: true,
      };
    } else {
      return {
        text: token,
        normalized: token,
        syllables: [token],
        isWord: false,
      };
    }
  });

  return {
    original: sentence,
    words,
  };
}
